import { useCallback, useEffect, useState } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';
import { useAgentWorkSignal } from '../../modules/vendor/work/agent-work.store';

/**
 * The endpoint still lives under content-studio.
 *
 * That is where the ledger was born, when generation was the only thing that
 * cost anything. Credits now pay for every AI action, and the service has moved
 * out — but the URL is what the deployed frontend calls, so it stays until
 * there is a reason to break it.
 */
const BALANCE = '/services/pms/content-studio/credits/balance';

/** Below this, the pill starts saying so rather than just showing a number. */
export const LOW_CREDITS = 25;

/**
 * What the vendor has left to spend on AI.
 *
 * Reloads when an agent run finishes, because that is exactly when the number
 * has changed: every model call is metered and debited, so a balance shown
 * before a conversation is stale by the end of it. Polling would ask a hundred
 * times to catch the one moment the signal already tells us about.
 */
export const useCreditBalance = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const finishedAt = useAgentWorkSignal((s) => s.finishedAt);

    const [balance, setBalance] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const response = (await sendAuthGuardedRequest(ApiMethod.GET, BALANCE)) as { balance?: number } | null;
            // A vendor with no ledger row reads as 0 rather than as unknown:
            // the server seeds on first read, so null here means the request
            // failed, and hiding the pill beats inventing a balance.
            setBalance(typeof response?.balance === 'number' ? response.balance : null);
        } catch {
            setBalance(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finishedAt]);

    return { balance, loading, reload: load, low: balance !== null && balance <= LOW_CREDITS };
};
